import {useEffect} from 'react'

import {type DocumentMaterialState} from '#/lib/document/material'
import {useNonReactiveCallback} from '#/lib/hooks/useNonReactiveCallback'

/**
 * Asking for a document's material, once, on the reader's own facts.
 *
 * A document's page table comes off the post, but the material behind it — the clips and
 * the questions themselves — is a separate record, fetched only when somebody asks for it.
 * This hook is that ask, and it is a *request* rather than a fact: the far end starts a
 * fetch, and whether calling it twice in a row is one fetch or two is the caller's business,
 * not the reader's to assume.
 *
 * Two moments ask, and both are keyed on what is true of the reader rather than on anything
 * about the caller:
 *
 *  - **The open.** A reader is where a document's questions are answered, and a document
 *    whose page table says it asks something is going to ask it whichever page the reader
 *    starts on — the door is unavoidable on the way past, by design. So the fetch belongs to
 *    the open, where its round trip is spent on the pages the reader is reading, rather than
 *    to the door, where the same round trip is spent on a spinner over the page they were
 *    about to read. A document with nothing to fetch — no questions and no clip anywhere in
 *    its table — asks for nothing, which is the case the lazy fetch was built for.
 *  - **A card that is already up on arrival.** Somebody who opened this reader *straight
 *    into* a page's questions (the composer's preview, the board's play) never asked the
 *    carousel's fetch to start, so the card is on screen waiting for questions nobody has
 *    gone to get. This one does not consult the page table: the card that is up names a page
 *    that asks, and a table older than the record is exactly how a page would ask something
 *    the table does not know about yet.
 *
 * The trap this hook exists for is the one `usePageReport` documents, one prop over. `request`
 * arrives from the card as an arrow written in the card's own body, so it is a *new function
 * on every render of the card* — and a live feed card renders for a like landing, a theme
 * change, a write of its own. An effect that names that function in its dependencies is then
 * an effect that runs again while `state` still reads `unasked`, which is exactly the window
 * a fetch that has not landed sits in: the reader asks a caller that is already asking, once
 * per render of a card nobody touched.
 *
 * Today's far end happens to be idempotent — the card hands over `setWantsMaterial(true)`,
 * and React bails out of a state write that changes nothing — so this is a trap rather than a
 * storm. Nothing in the reader's contract says the far end has to be: a request that starts a
 * fetch, logs an event or shows a spinner is one write per call, and the reader is the one
 * that decides how many calls there are.
 *
 * `useNonReactiveCallback` gives the caller's latest function one stable identity, so the ask
 * fires when a fact about the reader changes — the material state, whether the document
 * carries anything, which page's card is up — and for nothing else. The stable asker is
 * *returned* rather than kept here on purpose: the reader's doors ask too, since a turn or a
 * scroll that walks into a page's questions fetches them on the spot, and one identity for
 * the request is what keeps those callbacks from changing under a parent that re-rendered.
 */
export function useMaterialRequest({
  carries,
  state,
  quizPage,
  request,
}: {
  /** Whether the document's own page table says there is anything to fetch. */
  carries: boolean
  /** What the fetch has to say about the material (`DocumentMaterialState`). */
  state: DocumentMaterialState
  /** The page whose questions are open, when one is. */
  quizPage: number | null
  /** The caller's request — what starts the fetch. Absent is a reader that cannot ask. */
  request?: () => void
}): () => void {
  const ask = useNonReactiveCallback(request)

  useEffect(() => {
    if (state !== 'unasked' || !carries) return
    ask()
  }, [ask, carries, state])

  useEffect(() => {
    if (quizPage !== null && state === 'unasked') ask()
  }, [ask, quizPage, state])

  return ask
}
